import { createApi } from "@reduxjs/toolkit/query/react";

import type {
  EmailPreview,
  EmailPreviewRequest,
  RequestedAutomationStatus,
  AutomationRun,
  AutomationRunStep,
  AutomationSummary,
} from "../../../../common/types/Automation";
import { createServiceBaseQuery } from "../../client/serviceBaseQuery";

// Automation reads go through nav's BFF (/api/automations), which proxies zeus and
// maps the wire shape to AutomationSummary. The client just consumes typed DTOs.
export const automationsService = createApi({
  reducerPath: "automationsService",
  baseQuery: createServiceBaseQuery("bffApiUri"),
  tagTypes: ["Automation"],
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    getAutomations: builder.query<AutomationSummary[], void>({
      query: () => ({ url: "/api/automations" }),
      providesTags: [{ type: "Automation", id: "LIST" }],
    }),
    getAutomationRuns: builder.query<AutomationRun[], string>({
      query: (automationId) => ({ url: `/api/automations/${automationId}/runs` }),
      providesTags: (_result, _err, automationId) => [
        { type: "Automation", id: `RUNS-${automationId}` },
      ],
      // Runs are scheduler-driven and change out of band, so never serve a
      // retained cache — drop it on unmount and refetch on every mount (below).
      keepUnusedDataFor: 0,
    }),
    runAutomation: builder.mutation<void, string>({
      query: (automationId) => ({
        url: `/api/automations/${automationId}/run`,
        method: "POST",
      }),
      invalidatesTags: (_result, _err, automationId) => [
        { type: "Automation", id: "LIST" },
        { type: "Automation", id: `RUNS-${automationId}` },
      ],
    }),
    updateAutomationStatus: builder.mutation<
      void,
      { automationId: string; status: RequestedAutomationStatus }
    >({
      query: ({ automationId, status }) => ({
        url: `/api/automations/${automationId}`,
        method: "PUT",
        body: { status },
      }),
      invalidatesTags: [{ type: "Automation", id: "LIST" }],
    }),
    deleteAutomation: builder.mutation<void, string>({
      query: (automationId) => ({
        url: `/api/automations/${automationId}`,
        method: "DELETE",
      }),
      async onQueryStarted(automationId, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          return;
        }
        dispatch(
          automationsService.util.updateQueryData(
            "getAutomations",
            undefined,
            (draft) => {
              const index = draft.findIndex((item) => item.automationId === automationId);
              if (index !== -1) draft.splice(index, 1);
            },
          ),
        );
      },
    }),

    // Unlike getAutomationRuns above, steps keep the default cache: a finished
    // run's steps are immutable. Freshness for still-running runs is handled
    // at the call site (RunSteps refetches per expand while running).
    getAutomationRunSteps: builder.query<AutomationRunStep[], string>({
      query: (runId) => ({ url: `/api/automations/runs/${runId}/steps` }),
    }),

    previewEmail: builder.query<EmailPreview, EmailPreviewRequest>({
      query: (body) => ({ url: "/api/automations/preview-email", method: "POST", body }),
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useGetAutomationsQuery,
  useGetAutomationRunsQuery,
  useGetAutomationRunStepsQuery,
  useRunAutomationMutation,
  useUpdateAutomationStatusMutation,
  useDeleteAutomationMutation,
  usePreviewEmailQuery,
} = automationsService;
